import { listInterview } from "@endpoints/interview.service";
import { groupedGeneralMeetings } from "../SchedulingSection/SchedulingSection";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  IGetInterviewByIdResponse,
} from "@endpoints/types/interview.type";
import { Months, ScheduledMeeting } from "./dummyData";
import { useSnackbar } from "notistack";
import { SearchIcon } from "lucide-react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

export function getMeetingStatus(item: IGetInterviewByIdResponse) {
  if (
    item?.interviewStatus !== "Declined" &&
      item?.slotSelectionType === "general"
      ? !item?.isCandidateConfirm && !item?.isCompanyConfirm
      : !item?.isCandidateConfirm && !item?.isRecruiterAgencyConfirm
  ) {
    return {
      label: "Action Required",
      textStyle: "text-orange-400",
      buttonStyle: "border-orange-400",
    };
  } else if (
    item?.interviewStatus !== "Declined" &&
      item?.slotSelectionType === "general"
      ? item?.isCandidateConfirm && item?.isCompanyConfirm
      : item?.isCandidateConfirm && item?.isRecruiterAgencyConfirm
  ) {
    return {
      label: "Accepted",
      textStyle: "text-green-500",
      buttonStyle: "border-green-500",
    };
  } else if (
    item?.interviewStatus !== "Declined" &&
      item?.slotSelectionType === "general"
      ? item?.isCompanyConfirm || item?.isRecruiterAgencyConfirm
      : item?.isRecruiterAgencyConfirm || item?.isCandidateConfirm
  ) {
    return {
      label: "Pending",
      textStyle: "text-blue-500",
      buttonStyle: "border-blue-500",
    };
  } else if (item?.interviewStatus === "Declined") {
    return {
      label: "Declined",
      textStyle: "text-red-500",
      buttonStyle: "border-red-500",
    };
  }
}

export function getStatusColors({ status }: { status?: 'confirmed' } = {}) {
  switch (status) {
    case 'confirmed':
      return 'text-[#6750A4] bg-white border-[#6750A4]'
    default:
      return 'text-green-600 bg-white border-green-600'
  }
}

const ScheduleCard = ({ item, title }) => {
  const navigate = useNavigate();
  const isSameMonth = (dayjs().format('MMMM') === title) && dayjs(item?.specificSlot?.startTime).isAfter(dayjs(), 'days')
  const isExpired = dayjs(item?.specificSlot?.startTime).isBefore(dayjs(), 'days')
  const isSameDay = dayjs(item?.specificSlot?.startTime).isSame(dayjs(), 'days')
  const dateTime = item?.specificSlot?.startTime
  const isHiringCompanyInvolved = !!item?.hiringCompanyId?._id;
  const attendees = isHiringCompanyInvolved ? ["RA", "CA", "HC"] : ["RA", "CA"];
  const buttonInfo = getMeetingStatus(item);
  const isConfirmed = item?.status === 'confirmed'

  return (
    <div
      // onClick={() => navigate(`/meeting-details?id=${item?._id}`)}
      className={`items-center grid grid-cols-[0.5fr_1.5fr] cursor-pointer flex-1`}
    >
      <div className="flex self-end pb-7">
        <div
          className={`h-22 w-[2px] ${isSameDay
            ? "bg-green-500"
            : isSameMonth
              ? "bg-blue-400"
              : "bg-transparent"
            } mr-2`}
        ></div>
        <div className="flex flex-col gap-3.5 h-full">
          <span className="font-semibold text-primary text-xs">{dayjs(dateTime)?.format('ddd Do')} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{dayjs(dateTime)?.format('MMMM')}</span>
          <span className="font-semibold text-primary text-xs">{dayjs(item?.specificSlot?.startTime).format('hh:mm a')} - {dayjs(item?.specificSlot?.endTime)?.format('hh:mm a')}</span>
          <div className="flex items-center gap-2">
            {
              !isExpired ?
                <>
                  <span className="text-xss text-light-black font-medium">Meeting in</span>
                  <span className="text-xss text-primary font-semibold italic">4hrs 30min</span>
                </>
                :
                <span className="text-xss text-light-black font-medium">Expired</span>
            }
          </div>
        </div>
      </div>
      <div className="flex items-end border-b py-5">
        <div className="flex flex-col gap-2 flex-1 h-full pb-2">
          <div className={`flex items-center justify-center rounded-md border-l-[3px] bg-gray-100 w-max px-2 py-0.5 font-medium text-xs border-y border-r ${getStatusColors({ status: item?.status })}`}>{item?.interviewStatus}</div>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-light-black font-medium">Meeting Type:</span>
              <span className="text-xs text-light-black font-bold">{item?.meetingMode}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xss text-light-black font-medium">Your attendance:</span>
              <span className="text-xss text-primary font-semibold italic">Not Required</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 flex-1 h-full">
          <div className="flex items-center gap-2">
            <span className="text-xs text-light-black font-medium">Title:</span>
            <span className="text-xs text-primary font-semibold">{item?.meetingTitle}</span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-light-black font-medium">Position:</span>
              <span className="text-xs text-primary font-medium">{item?.jobTitle}</span>
            </div>
            <div className="flex items-center gap-5">
              <span className="text-xss font-medium text-light-black">Attendees</span>
              <div className="flex items-center gap-2">
                {
                  ['RA', 'CA', 'HC']?.map((item) => {
                    return <div className="h-8 w-8 flex items-center justify-center border-[2px] border-gray-100 rounded-full text-xs text-primary font-semibold">{item}</div>
                  })
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MonthSection = ({ activeMonth, schedulingPolls, onClick }) => {
  const currentMonth = dayjs().format('MMMM')

  return <div className="flex flex-col w-32 border rounded-md h-max shadow-[0px_4px_4px_4px_rgba(0,0,0,0.03)] px-2">
    {
      Months?.map((month, monthIndex) => {
        const isCurrentMonth = month === currentMonth
        const isActiveMonth = month === activeMonth
        const dataCount = schedulingPolls?.find((item) => item?.title === month)?.data?.length
        return <div className={`w-full ${monthIndex !== 0 ? 'border-t' : ''}`}>
          <button onClick={() => onClick(month)} className={`flex w-full my-1 items-center gap-1 h-7 text-xs hover:bg-gray-50 rounded-md overflow-hidden pr-2
          ${isActiveMonth ? 'bg-[#F1F5F9]' : ''}
         `}>
            <div className={`h-full w-[2px] ${isCurrentMonth ? 'bg-secondary' : 'bg-transparent'} rounded-md`} />
            {month}
            {dataCount ?
              <div className="ml-auto text-primary">{dataCount}</div>
              :
              null}
          </button>
        </div>
      })
    }
  </div>
}

const ScheduleSection1 = () => {
  const { enqueueSnackbar } = useSnackbar()
  const itemRefs = useRef([])
  const scrollRef = useRef(null)
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [activeMonth, setActiveMonth] = useState('')
  const [schedulingPolls, setSchedulingPolls] = useState([]);

  useEffect(() => {
    if (schedulingPolls?.length) {
      const title = dayjs().format('MMMM YYYY')
      itemRefs.current[title]?.scrollIntoView({
        behavior: "smooth",
        block: 'center',
      });
    }
    if (!scrollRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first section that is intersecting from the top
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const title = (entry.target as HTMLElement).dataset.title;
            if (title) {
              setActiveMonth(title);
            }
          }
        });
      },
      {
        root: scrollRef.current,
        // Create a detection line at the top of the container
        // This triggers when a section's top edge crosses the top of the scroll container
        rootMargin: '-1px 0px -99% 0px',
        threshold: 0,
      }
    );

    Object.values(itemRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();

  }, [schedulingPolls])

  useEffect(() => {
    // @ts-ignore
    // setSchedulingPolls(groupedGeneralMeetings(ScheduledMeeting))
    listInterview({
      isSchedule: true,
    })
      .then((response) => {
        const { success, data } = response;
        if (success) {
          // data?.map((item) => {
          //   deleteInterview(item?._id);
          // });
          setSchedulingPolls(() => groupedGeneralMeetings(data));
          console.log('schedulingPolls', data, groupedGeneralMeetings(data))
          // setEvents(() => formatEvents(data));
        }
      })
      .finally(() => setIsEventLoading(false));
  }, []);


  return (
    <div className="flex flex-col flex-1 w-full min-h-0">
      {/* Date and Search section */}
      <div className="flex items-center gap-10 mb-5">
        {/* Current date section */}
        <button
          onClick={() => {
            itemRefs.current[dayjs().format('MMMM')]?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }}
          className="flex items-center gap-1">
          <div className="h-8 w-[2px] bg-[#31AA52]" />
          <div className="text-xs font-semibold text-primary italic rounded-md border-2 h-8 px-2 flex items-center justify-center">{dayjs().format('dddd DD of MMMM YYYY')}</div>
        </button>
        {/* Search section */}
        <div className="flex items-center gap-1 rounded-md border px-2.5 h-9 bg-gray-50">
          <input className="w-72 h-full text-xs font-medium bg-transparent outline-none"
            placeholder="Search"
          />
          <SearchIcon size={15} color="gray" />
        </div>
      </div>
      <div className="flex min-h-0 gap-12">
        <div className="flex flex-col flex-1 min-h-0">
          {/* Sticky header showing active month */}
          {activeMonth && (
            <div className="grid grid-cols-[0.48fr_1.52fr] items-center gap-2">
              <div className="flex items-center gap-2 font-bold text-primary text-sm pl-2 w-[12.5rem]">
                {activeMonth}
                <div className="h-px flex-1 bg-primary" />
              </div>
              <div
                className={`flex items-center py-1.5 px-3 gap-4 bg-[#F1F5F9] rounded-md z-10 flex-1`}
              >
                <label className="font-bold text-sm m-auto">{activeMonth}</label>
              </div>
            </div>
          )}
          {
            isEventLoading ?
              <div className="flex flex-1 justify-center items-center">
                <LoadingAnimation className="w-8 h-8" color="black" />
              </div>
              :
              !schedulingPolls?.length ?
                <div className="flex flex-1 justify-center items-center">
                  <span className="font-medium text-sm text-light-black">No meeting available at the moment.</span>
                </div>
                :
                <div
                  ref={scrollRef}
                  className="min-h-0 flex-col flex flex-1 overflow-y-scroll no-scrollbar mt-1.5">
                  <div
                    className="flex flex-col gap-7">
                    {
                      schedulingPolls?.map((item, itemIndex) => {
                        return <div key={item?.title || itemIndex}
                          ref={(el) => {
                            if (el) itemRefs.current[item?.title || itemIndex] = el
                          }}
                          data-title={item?.title}
                          className="flex flex-col">
                          {itemIndex !== 0 && item?.title !== activeMonth ?
                            <div className="flex items-center gap-8 font-bold text-primary text-sm pl-2">
                              {item?.title}
                              <div className="h-px flex-1 bg-primary" />
                            </div>
                            :
                            null}
                          {!item?.data?.length ?
                            <span className="text-xs font-medium m-auto mt-4 text-light-black">No meeting scheduled</span>
                            :
                            item?.data.map((meeting) => (
                              <ScheduleCard
                                key={meeting?._id}
                                item={meeting}
                                title={item?.title}
                              />
                            ))
                          }
                        </div>
                      })
                    }
                  </div>
                </div>
          }
        </div>
        <MonthSection activeMonth={activeMonth} schedulingPolls={schedulingPolls} onClick={(month) => {
          const hasData = schedulingPolls?.find((item) => item?.title === month)?.data?.length
          if (!hasData) {
            enqueueSnackbar('No meeting available on this month', { variant: 'warning' })
          } else {
            // Scroll to the top of the clicked month's group
            itemRefs.current[month]?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
        }} />
      </div>
    </div>
  );
};

export default ScheduleSection1;
